package pl.decks.personal.pages.ievp.sections.insights

import androidx.compose.runtime.*
import androidx.compose.runtime.snapshots.SnapshotStateMap
import com.varabyte.kobweb.compose.css.Animation
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.onAnimationEnd
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.icons.fa.FaEye
import com.varabyte.kobweb.silk.components.icons.fa.FaEyeSlash
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import pl.decks.personal.extensions.animationNotNull
import kotlin.time.Duration.Companion.milliseconds

private enum class RevealingInsightState {
    HIDDEN,
    REVEALED,
    HIDING,
    ICON_SWAP;

    fun next(): RevealingInsightState = when (this) {
        HIDDEN -> REVEALED
        REVEALED -> HIDING
        HIDING -> ICON_SWAP
        ICON_SWAP -> HIDDEN
    }

    @Composable
    fun stateChangeAnimation(): Animation.Listable? = when (this) {
        HIDDEN, ICON_SWAP -> null
        else -> InsightsSectionStylesheet.UnfoldFrames.toAnimation(
            duration = 2 * InsightsSectionStylesheet.Vars.animationStepDuration.value(),
            timingFunction = AnimationTimingFunction.EaseInOut,
            delay = null,
            direction = if (this == REVEALED) AnimationDirection.Normal else AnimationDirection.Reverse,
            fillMode = AnimationFillMode.Both,
        )
    }
}

private suspend fun SnapshotStateMap<Insight, RevealingInsightState>.hide(at: Insight) {
    val transientState = this[at]?.next() ?: return
    this[at] = transientState
    delay(1.milliseconds)
    this[at] = transientState.next()
}

@OptIn(DelicateApi::class)
@Composable
fun InsightsSectionContent(sectionProgress: Float) {
    val coroutineScope = rememberCoroutineScope()
    val breakpoint = rememberBreakpoint()
    val insightHeight = if (breakpoint < Breakpoint.MD) 100f / 2.5f else 100f / 4
    val totalProgressVHUnits = 100f + insightHeight * Insight.entries.size.plus(1)
    val displayStart = 80f
    val revealStep = 1.3 * insightHeight

    val states = remember {
        mutableStateMapOf(*Insight.entries.map { it to RevealingInsightState.HIDDEN }.toTypedArray())
    }

    LaunchedEffect(sectionProgress) {
        states.forEach { (insight, currentState) ->
            val visibleBreakpoint = displayStart + insight.ordinal * revealStep
            val isVisible = sectionProgress * totalProgressVHUnits > visibleBreakpoint
            states[insight] = when (currentState) {
                RevealingInsightState.HIDDEN if isVisible -> currentState.next()
                RevealingInsightState.REVEALED if !isVisible -> currentState.next()
                else -> currentState
            }
        }
    }

    Column(
        modifier = InsightsSectionStylesheet.Section.toModifier()
            .setVariable(InsightsSectionStylesheet.Vars.unfoldedMaxHeight, insightHeight.vh),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Insight.entries.forEach { insight ->
            RevealingInsight(
                state = states[insight] ?: RevealingInsightState.HIDDEN,
                isFlipped = breakpoint >= Breakpoint.MD && insight.ordinal % 2 == 1,
                onHidingFinished = { coroutineScope.launch { states.hide(insight) } },
                content = insight.content,
            )
        }
    }
}

@Composable
private fun RevealingInsight(
    state: RevealingInsightState,
    onHidingFinished: () -> Unit,
    isFlipped: Boolean,
    content: @Composable () -> Unit,
) = Box(
    modifier = InsightsSectionStylesheet.Entry.UnfoldedContainer.toModifier(),
    contentAlignment = Alignment.Center,
) {
    var showContent by remember(state) { mutableStateOf(false) }
    key(state == RevealingInsightState.HIDING) {
        Box(
            modifier = InsightsSectionStylesheet.Entry.Folded.toModifier()
                .thenIf(isFlipped) { Modifier.flexDirection(FlexDirection.RowReverse) }
                .animationNotNull(state.stateChangeAnimation())
                .onAnimationEnd {
                    if (state == RevealingInsightState.REVEALED) showContent = true
                    if (state == RevealingInsightState.HIDING) onHidingFinished()
                },
            contentAlignment = Alignment.Center,
        ) {
            InsightIcons(isHidden = state == RevealingInsightState.HIDDEN)
            if (showContent) Box(InsightsSectionStylesheet.Entry.Content.toModifier()) {
                content()
            }
        }
    }
}

@Composable
private fun InsightIcons(isHidden: Boolean) = Box(contentAlignment = Alignment.Center) {
    FaEye(InsightsSectionStylesheet.Entry.Icon.toModifier().opacity(if (isHidden) 0f else 1f))
    FaEyeSlash(InsightsSectionStylesheet.Entry.Icon.toModifier().opacity(if (isHidden) 0.3f else 0f))
}
